// Cadastro de Usuário e login (base pro backend hospedado — não tem tela
// ainda). O domínio nunca calcula nem confere hash de senha: recebe o hash
// já pronto (bcrypt, calculado por quem chama — `server/`) e só guarda e
// compara **hashes**, nunca senha em texto puro. Mantém o domínio livre de
// dependência externa (ADR-0001).

import { newId, type Id } from "./ids.ts";
import type { Snapshot, Usuario } from "./snapshot.ts";

export type { Usuario };

export class ErroUsuario extends Error {}

export interface DadosUsuario {
  nome: string;
  login: string;
  /** Hash bcrypt já calculado — nunca a senha em si. */
  senhaHash: string;
  perfilId: Id | null;
}

function loginNormalizado(login: string): string {
  return login.trim().toLowerCase();
}

function mesmoLogin(u: Pick<Usuario, "login">, alvo: string): boolean {
  return u.login != null && loginNormalizado(u.login) === alvo;
}

export interface DadosEdicaoUsuario {
  nome: string;
  login: string;
  perfilId: Id | null;
}

/** Regras comuns a criar e editar: nome, login (único), perfil existe. */
function validarNomeLoginPerfil(
  dados: DadosEdicaoUsuario,
  snapshot: Snapshot,
  ignorarId?: Id,
): { nome: string; login: string; perfilId: Id | null } {
  const nome = dados.nome.trim();
  if (nome === "") throw new ErroUsuario("Informe o nome do usuário.");

  const login = loginNormalizado(dados.login);
  if (login === "") throw new ErroUsuario("Informe o login do usuário.");
  if (snapshot.usuarios.some((u) => u.id !== ignorarId && mesmoLogin(u, login))) {
    throw new ErroUsuario(`O login "${login}" já está em uso.`);
  }
  if (dados.perfilId != null && !snapshot.perfis.some((p) => p.id === dados.perfilId)) {
    throw new ErroUsuario("Perfil não encontrado.");
  }
  return { nome, login, perfilId: dados.perfilId };
}

export function criarUsuario(snapshot: Snapshot, dados: DadosUsuario): Snapshot {
  const d = validarNomeLoginPerfil(dados, snapshot);
  if (dados.senhaHash.trim() === "") {
    throw new ErroUsuario("Senha não informada.");
  }
  const usuario: Usuario = {
    id: newId(),
    ...d,
    senhaHash: dados.senhaHash,
    ativo: true,
  };
  return { ...snapshot, usuarios: [...snapshot.usuarios, usuario] };
}

export function editarUsuario(
  snapshot: Snapshot,
  id: Id,
  dados: DadosEdicaoUsuario,
): Snapshot {
  if (!snapshot.usuarios.some((u) => u.id === id)) {
    throw new ErroUsuario("Usuário não encontrado.");
  }
  const d = validarNomeLoginPerfil(dados, snapshot, id);
  return {
    ...snapshot,
    usuarios: snapshot.usuarios.map((u) => (u.id === id ? { ...u, ...d } : u)),
  };
}

/** Quantos usuários ativos sobram se `exceto` sair de cena. */
function ativosSem(snapshot: Snapshot, exceto: Id): number {
  return snapshot.usuarios.filter((u) => u.ativo && u.id !== exceto).length;
}

export function excluirUsuario(snapshot: Snapshot, id: Id): Snapshot {
  const alvo = snapshot.usuarios.find((u) => u.id === id);
  if (!alvo) throw new ErroUsuario("Usuário não encontrado.");
  if (alvo.ativo && ativosSem(snapshot, id) === 0) {
    throw new ErroUsuario("Não dá para excluir o único usuário ativo.");
  }
  return { ...snapshot, usuarios: snapshot.usuarios.filter((u) => u.id !== id) };
}

export function definirSenhaHash(
  snapshot: Snapshot,
  id: Id,
  senhaHash: string,
): Snapshot {
  if (!snapshot.usuarios.some((u) => u.id === id)) {
    throw new ErroUsuario("Usuário não encontrado.");
  }
  if (senhaHash.trim() === "") throw new ErroUsuario("Senha não informada.");
  return {
    ...snapshot,
    usuarios: snapshot.usuarios.map((u) => (u.id === id ? { ...u, senhaHash } : u)),
  };
}

export function definirAtivoUsuario(
  snapshot: Snapshot,
  id: Id,
  ativo: boolean,
): Snapshot {
  if (!snapshot.usuarios.some((u) => u.id === id)) {
    throw new ErroUsuario("Usuário não encontrado.");
  }
  if (!ativo && ativosSem(snapshot, id) === 0) {
    throw new ErroUsuario("Não dá para inativar o único usuário ativo.");
  }
  return {
    ...snapshot,
    usuarios: snapshot.usuarios.map((u) => (u.id === id ? { ...u, ativo } : u)),
  };
}

/** O Usuário dono deste login, ativo ou não — pra administração (ex.: redefinir senha). */
export function usuarioPorLogin(
  snapshot: Pick<Snapshot, "usuarios">,
  login: string,
): Usuario | null {
  const alvo = loginNormalizado(login);
  return snapshot.usuarios.find((u) => mesmoLogin(u, alvo)) ?? null;
}

/**
 * O Usuário **ativo** dono deste login, se existir — devolve o `senhaHash`
 * pra quem chamou conferir contra a senha recebida (bcrypt fica em
 * `server/`). Não diferencia "login não existe" de "está inativo": a
 * mensagem de erro de autenticação não deve dar essa pista a quem está
 * tentando entrar.
 */
export function usuarioParaLogin(
  snapshot: Pick<Snapshot, "usuarios">,
  login: string,
): Usuario | null {
  const usuario = usuarioPorLogin(snapshot, login);
  return usuario && usuario.ativo ? usuario : null;
}
